import { Dispatch, SetStateAction, useCallback } from 'react';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { Sort } from '@/types/sort.type';
import { useSelectionProductsAction } from '@/components/action-buttons/use-selection-products-action';
import { useSelectionBrandsAction } from '@/components/action-buttons/use-selection-brands-action';
import { useSelectionReviewsAction } from '@/components/action-buttons/use-selection-reviews-action';
import type { ActionButtonsActiveTable } from '@/components/action-buttons/action-buttons-view.types';

type UseSelectionActionsParams = {
    activeTable: ActionButtonsActiveTable;
    accessToken: string;
    selectionSessionId: string;
    hasPickedProducts: boolean;
    hasPickedBrandGroups: boolean;
    products: ProductGroupEntity[];
    brandGroups: BrandGroupEntity[];
    maxSelectedItems: number | null;
    total: number;
    filtersQuery: string[];
    sort: Sort;
    setPickedProducts?: (val: string[]) => void;
    setPickedBrandGroups?: (val: string[]) => void;
    setPickedReviews?: (val: string[]) => void;
    waitForReviewSelectionReconcile: () => Promise<void>;
    enqueueReviewSelectionMutation: <T>(mutation: () => Promise<T>) => Promise<T>;
    getCurrentPickedReviews: () => string[];
    setIsAllPickLoading: Dispatch<SetStateAction<boolean>>;
};

type UseSelectionActionsReturn = {
    onAllPicked: () => Promise<void>;
};

export const useSelectionActions = ({
    activeTable,
    accessToken,
    selectionSessionId,
    hasPickedProducts,
    hasPickedBrandGroups,
    products,
    brandGroups,
    maxSelectedItems,
    total,
    filtersQuery,
    sort,
    setPickedProducts,
    setPickedBrandGroups,
    setPickedReviews,
    waitForReviewSelectionReconcile,
    enqueueReviewSelectionMutation,
    getCurrentPickedReviews,
    setIsAllPickLoading,
}: UseSelectionActionsParams): UseSelectionActionsReturn => {
    const { onAllPickedProducts } = useSelectionProductsAction({
        accessToken,
        selectionSessionId,
        hasPickedProducts,
        products,
        maxSelectedItems,
        setPickedProducts,
    });
    const { onAllPickedBrandGroups } = useSelectionBrandsAction({
        hasPickedBrandGroups,
        brandGroups,
        maxSelectedItems,
        setPickedBrandGroups,
    });
    const { onAllPickedReviews } = useSelectionReviewsAction({
        accessToken,
        selectionSessionId,
        total,
        filtersQuery,
        sort,
        maxSelectedItems,
        setPickedReviews,
        waitForReviewSelectionReconcile,
        enqueueReviewSelectionMutation,
        getCurrentPickedReviews,
        setIsAllPickLoading,
    });

    const onAllPicked = useCallback(async () => {
        if (activeTable === 'products') {
            await onAllPickedProducts();
            return;
        }

        if (activeTable === 'brands') {
            await onAllPickedBrandGroups();
            return;
        }

        await onAllPickedReviews();
    }, [activeTable, onAllPickedBrandGroups, onAllPickedProducts, onAllPickedReviews]);

    return {
        onAllPicked,
    };
};
